import { useCallback, useMemo } from 'react'
import { MoreHorizontal } from 'lucide-react'

import { SmartAnalysisLeaderboard } from '@/components/search/smart-analysis-leaderboard'
import { SmartAnalysisScoreboard } from '@/components/search/smart-analysis-scoreboard'
import { useArtifactCanvas } from '@/components/artifact/artifact-canvas-context'
import { ArtifactCanvasBody } from '@/components/artifact/artifact-canvas-layout'
import { EvaluationAnalysisView } from '@/components/evaluation/evaluation-analysis-view'
import { MatchQualityBadge } from '@/components/evaluation/match-quality-badge'
import { PersonListCard } from '@/components/person-list-card'
import { ListCard } from '@/components/list-card'
import { ListCardHeader } from '@/components/list-card-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  EmploymentReportCanvas,
  dataBenchDisplayName,
} from '@/components/platform/platform-data-bench-person-canvas'
import type {
  SimulationCondition,
  SimulationProfile,
  SimulationScenario,
} from '@/hooks/use-platform-simulation'
import type { ApplicantCard } from '@/hooks/use-opportunity-applicants'
import type { UnifiedCandidate } from '@/hooks/use-smart-search'
import type { DataBenchCopy } from '@/lib/platform-data-bench-copy'

const CONDITION_REPORT_COPY = {
  scoreSuffix: 'self-management coverage',
  highlightsTitle: "What's going well",
  redFlagsTitle: 'Discuss with a practitioner',
}

function poolProfilesToApplicants(
  profiles: SimulationProfile[],
  opportunityId: string,
): ApplicantCard[] {
  const now = new Date().toISOString()
  return profiles.map((profile) => ({
    id: profile.evaluation_id || profile.id,
    profile: {
      id: profile.id,
      name: dataBenchDisplayName(profile),
      headline: profile.headline || null,
      avatar_initials: null,
    },
    opportunity_id: opportunityId,
    status: 'applied',
    source: null,
    current_stage: null,
    days_in_stage: null,
    applied_at: profile.scored_at || now,
    updated_at: profile.scored_at || now,
    has_thread: false,
    event_count: 0,
    match_score: profile.final_score ?? null,
    evaluation_id: profile.evaluation_id,
    board_position: profile.rank ?? null,
  }))
}

function poolProfilesToCandidates(profiles: SimulationProfile[]): UnifiedCandidate[] {
  return profiles
    .filter((profile) => profile.final_score != null)
    .map((profile) => ({
      profile_id: profile.id,
      name: dataBenchDisplayName(profile),
      score: profile.final_score ?? 0,
      rank: profile.rank ?? undefined,
      state: 'analyzed' as const,
      full: {
        rank: profile.rank ?? 0,
        profile_id: profile.id,
        candidate_name: dataBenchDisplayName(profile),
        final_score: profile.final_score ?? 0,
        structured_score: profile.final_score ?? 0,
        semantic_score: profile.final_score ?? 0,
        was_llm_judged: true,
        evaluation_id: profile.evaluation_id,
      },
    }))
}

function sortProfiles(profiles: SimulationProfile[]): SimulationProfile[] {
  return [...profiles].sort((a, b) => {
    const scoreA = a.final_score ?? -1
    const scoreB = b.final_score ?? -1
    if (scoreB !== scoreA) return scoreB - scoreA
    const rankA = a.rank ?? 10 ** 9
    const rankB = b.rank ?? 10 ** 9
    return rankA - rankB
  })
}

function ConditionReportCanvas({ condition }: { condition: SimulationCondition }) {
  const label = condition.patient_name
    ? `${condition.name} · ${condition.patient_name}`
    : condition.name
  if (!condition.evaluation_id) {
    return (
      <ArtifactCanvasBody>
        <p className="text-sm text-muted-foreground">No report for this condition yet.</p>
      </ArtifactCanvasBody>
    )
  }
  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <p className="mb-4 text-sm text-muted-foreground">
          Condition self-management sample report (non-diagnostic).
        </p>
        <EvaluationAnalysisView
          evaluationId={condition.evaluation_id}
          subjectLabel={label}
          copy={CONDITION_REPORT_COPY}
        />
      </div>
    </ArtifactCanvasBody>
  )
}

export function DataBenchResults({
  scenario,
  copy,
  craftEnabled,
  onToggleKnownGood,
}: {
  scenario: SimulationScenario
  copy: DataBenchCopy
  craftEnabled: boolean
  onToggleKnownGood: (profileId: string) => void
}) {
  const { openCanvas } = useArtifactCanvas()
  const isEmployment = scenario.methodology_id === 'employment_match'
  const profiles = useMemo(() => sortProfiles(scenario.profiles ?? []), [scenario.profiles])
  const conditions = scenario.conditions ?? []
  const scoredCount = isEmployment
    ? profiles.filter((p) => p.evaluation_id).length
    : conditions.filter((c) => c.evaluation_id).length
  const summary = scenario.last_score_summary
  const poolApplicants = useMemo(
    () => poolProfilesToApplicants(profiles, scenario.opportunity_id || scenario.id),
    [profiles, scenario.opportunity_id, scenario.id],
  )
  const poolCandidates = useMemo(() => poolProfilesToCandidates(profiles), [profiles])
  const poolSummary = scenario.pool_cohort_summary?.summary?.trim() || ''

  const openProfile = useCallback(
    (profile: SimulationProfile) => {
      openCanvas({
        title: dataBenchDisplayName(profile),
        preventAutoFocus: true,
        content: (
          <EmploymentReportCanvas
            profile={profile}
            opportunityTitle={scenario.opportunity_title}
          />
        ),
      })
    },
    [openCanvas, scenario.opportunity_title],
  )

  const openCondition = useCallback(
    (condition: SimulationCondition) => {
      openCanvas({
        title: condition.name,
        preventAutoFocus: true,
        content: <ConditionReportCanvas condition={condition} />,
      })
    },
    [openCanvas],
  )

  if (scenario.status !== 'scored' && scoredCount === 0) {
    return (
      <section className="space-y-2">
        <h2 className="text-base font-semibold">Results</h2>
        <p className="text-sm text-muted-foreground">
          Score this run to see ranked {copy.poolNoun} here. Results stay on this page — you do not
          need to open Pipeline or Rubrics.
        </p>
      </section>
    )
  }

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold">Results</h2>
        <p className="text-sm text-muted-foreground">
          {summary
            ? `${summary.evaluated} of ${summary.total} scored.`
            : `${scoredCount} scored.`}{' '}
          Open a row for the full report.
        </p>
        {summary?.errors?.length ? (
          <p className="mt-1 text-sm text-destructive">{summary.errors.slice(0, 3).join(' · ')}</p>
        ) : null}
      </div>

      {isEmployment && (poolSummary || poolCandidates.length > 0) ? (
        <div className="space-y-4">
          <SmartAnalysisScoreboard
            applicants={poolApplicants}
            summary={poolSummary || null}
            metaMode="pool"
          />
          {poolCandidates.length > 0 ? (
            <SmartAnalysisLeaderboard
              variant="standalone"
              candidates={poolCandidates}
              onOpenPerson={(profileId) => {
                const profile = profiles.find((row) => row.id === profileId)
                if (profile) openProfile(profile)
              }}
            />
          ) : null}
        </div>
      ) : null}

      {isEmployment ? (
        <ul className="space-y-2">
          {profiles.map((profile) => (
            <li key={profile.id}>
              <PersonListCard
                variant="evaluation"
                person={{
                  id: profile.id,
                  name: dataBenchDisplayName(profile),
                  subtitle: profile.headline || profile.email,
                  colorSeed: profile.id,
                }}
                onOpen={() => openProfile(profile)}
                openAriaLabel={`Open report for ${dataBenchDisplayName(profile)}`}
                headerAside={
                  profile.final_score != null ? (
                    <MatchQualityBadge score={profile.final_score} />
                  ) : (
                    <span className="text-xs text-muted-foreground">Not scored</span>
                  )
                }
                titleBadge={
                  profile.is_needle
                    ? { label: 'Known good', variant: 'secondary' }
                    : null
                }
                menu={
                  craftEnabled ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button type="button" variant="ghost" size="iconSm" aria-label="More">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onToggleKnownGood(profile.id)}>
                          {profile.is_needle ? 'Unmark known good' : 'Mark as known good'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : null
                }
              />
            </li>
          ))}
        </ul>
      ) : (
        <ul className="space-y-2">
          {conditions.map((condition) => (
            <li key={condition.id}>
              <ListCard
                header={
                  <div className="flex min-w-0 items-start justify-between gap-3">
                    <ListCardHeader title={condition.name} size="sm" className="min-w-0 flex-1" />
                    {condition.final_score != null ? (
                      <MatchQualityBadge
                        score={condition.final_score}
                        percentSuffix="coverage"
                      />
                    ) : (
                      <Badge variant="outline">No report</Badge>
                    )}
                  </div>
                }
                body={
                  condition.patient_name ? (
                    <p className="text-sm text-muted-foreground">{condition.patient_name}</p>
                  ) : null
                }
                onOpen={
                  condition.evaluation_id ? () => openCondition(condition) : undefined
                }
                openAriaLabel={
                  condition.evaluation_id
                    ? `Open report for ${condition.name}`
                    : undefined
                }
              />
            </li>
          ))}
          {conditions.length === 0 ? (
            <li className="px-4 py-4 text-sm text-muted-foreground">
              No conditions on this run. Create a scoring sample pack first.
            </li>
          ) : null}
        </ul>
      )}
    </section>
  )
}
